import { callWithNuxt } from 'nuxt/app';
import { getIsAppInstalled } from '~/lib/sync/isAppInstalled';

export const useAppInit = async (nextUri: string = null) => {
  globalThis.$logger.info(
    'useAppInit :: start app init',
    useAppRunningStore().isRunning,
    nextUri
  );

  const profileLoadFromNetwork = useProfileLoadFromNetwork();

  const runtimeConfig = useRuntimeConfig();

  if (!useAppRunningStore().isRunning) return;

  globalThis.$logger.info('useAppInit :: start app init continue');

  const nuxtApp = useNuxtApp();

  const context = useContextStore();

  const user = useUserStore();
  const profile = useProfileStore();

  const route = useRoute();

  const { query } = route;

  const error = useError();

  const auth: boolean =
    route.meta.auth != null ? (route.meta.auth as boolean) : true;

  const loadConfig: boolean =
    route.meta.config != null ? (route.meta.config as boolean) : true;

  const locked: boolean =
    route.meta.locked != null ? (route.meta.locked as boolean) : false;

  const forceLogout =
    route.meta.forceLogout != null ? route.meta.forceLogout : true;

  const continuePath = !auth || locked ? '/' : route.fullPath;

  const redirectTo = async (path: string, addContinue = true) => {
    if (path === route.path) return;

    let ctn = route.query?.continue ?? continuePath;

    if (error.value) {
      clearError();
      ctn = '/';
    }

    let query = {};
    if (addContinue) {
      query = {
        continue: nextUri || ctn,
      };
    } else {
      const protocol = window.location.protocol;
      const host = window.location.host;
      let href = `${protocol}//${host}`;
      href = nextUri ? `${href}${nextUri}` : href;
      const url = new URL(href);
      query = Object.fromEntries(url.searchParams);
      path = `${url.pathname}`;
    }

    const external = path ? path.includes(runtimeConfig.public.eva1Url) : false;
    await callWithNuxt(nuxtApp, navigateTo, [
      {
        path,
        query,
      },
      {
        replace: true,
        external,
      },
    ]);
  };

  const getNextRouteUri = () => {
    let nextRouteUri = nextUri;
    nextRouteUri = query.continue ? (query.continue as string) : nextRouteUri;
    if (nextRouteUri && nextRouteUri.includes('/premium'))
      nextRouteUri = `${runtimeConfig.public.eva1Url}${nextRouteUri}`;
    return nextRouteUri;
  };

  const process = async () => {
    globalThis.$logger.info(
      'useAppInit :: process, isLoggedIn',
      context.isLoggedIn,
      'user data',
      user.data
    );
    if (!context.isLoggedIn || !user.data) {
      // not logged in or no user associated

      if (auth) {
        globalThis.$logger.info(
          'useAppInit :: this is a auth page but user is not conntected, we redirect to signin'
        );
        return await redirectTo('/signin');
      } else {
        globalThis.$logger.info(
          'useAppInit :: this is not a auth page and user is not conntected, we do nothing'
        );
      }
      // } else if (!profile.data || profile.needToRefreshProfiling) {
    } else if (!profile.data) {
      // no profile data or need to refresh profiling
      globalThis.$logger.info(
        'useAppInit :: user is connected but has no profile data or needToRefreshProfiling so we load profile from network'
      );

      const { hasBeenRefreshed, error, status } =
        await profileLoadFromNetwork.run(false);

      globalThis.$logger.info(
        'useAppInit :: profile loaded',
        hasBeenRefreshed,
        error,
        status
      );
      const is409 = status === 409;
      if (profile.needToRefreshProfiling || is409) {
        if (is409) {
          globalThis.$logger.info(
            'useAppInit :: profile has been loaded by network but we got a 409'
          );
        } else {
          globalThis.$logger.info(
            'useAppInit :: profile has been loaded by network but it still need to be refreshing',
            profile.needToRefreshProfiling
          );
        }
        globalThis.$logger.info(
          'useAppInit :: redirect to profiling',
          `/profiling`
        );
        return await redirectTo('/profiling');
      } else if (error) {
        globalThis.$logger.info('useAppInit :: error loading profile', error);
        return (window.location.href = '/logout');
      } else {
        globalThis.$logger.info(
          'useAppInit :: profile loaded now restarting process'
        );
        return await process();
      }
    } else {
      globalThis.$logger.info('useAppInit :: profile ok');

      if (profile.needToDefineMainContent) {
        globalThis.$logger.info(
          'useAppInit :: profile.needToDefineMainContent'
        );
        // await useAppInstallMin().install();
        const appIsInstalled = await getIsAppInstalled(
          nuxtApp.$db,
          profile.contents
        );

        if (!appIsInstalled) {
          globalThis.$logger.info(
            'useAppInit :: app is not installed so we do so'
          );
          return await redirectTo('/installation');
        } else {
          globalThis.$logger.info(
            'useAppInit :: app is installed we redirect to nomad'
          );
          return await redirectTo('/nomad');
        }
      }
      // user and profile, check if app has ever been installed
      const appIsInstalled = await getIsAppInstalled(
        nuxtApp.$db,
        profile.contents
      );

      if (!appIsInstalled) {
        globalThis.$logger.info(
          'useAppInit :: app is not installed, we redirect to installation'
        );
        return await redirectTo('/installation');
      }
      if (!auth) {
        if (forceLogout) {
          globalThis.$logger.info(
            'useAppInit :: the current page is not an auth page but the user is connected we redirect to the home'
          );
          const nextRouteUri = getNextRouteUri();
          return await redirectTo(nextRouteUri, false);
        } else {
          globalThis.$logger.info(
            'useAppInit :: the current page is not an auth page and forceLogout is false so we stay on the page'
          );
        }
      } else {
        const nextRouteUri = getNextRouteUri();

        if (nextRouteUri) {
          globalThis.$logger.info(
            'useAppInit :: the user is connected the page is ok. We have a nextRouteUri so we redirect to it',
            nextRouteUri
          );
          return await redirectTo(nextRouteUri, false);
        } else {
          globalThis.$logger.info(
            'useAppInit :: the user is connected the page is ok. We do nothing'
          );
        }
      }
    }
  };
  await context.initClient(loadConfig);
  await process();
};
